import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../../../config";
import RoleDashboard from "./RoleDashboard";
import LogVisitModal from "./LogVisitModal";
import m from "./NurseDashboard.module.css";

const AVATAR_PALETTE = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}
function initials(first, last) {
  const a = (first || "").trim()[0] || "";
  const b = (last  || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

function fmtRelative(iso) {
  if (!iso) return "";
  const ms  = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return new Date(iso).toLocaleString();
  const min = Math.floor(ms / 60000);
  if (min < 1)  return "just now";
  if (min < 60) return `${min}m ago`;
  const hr  = Math.floor(min / 60);
  if (hr  < 24) return `${hr}h ago`;
  const d   = Math.floor(hr / 24);
  if (d   < 7)  return `${d}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return new Date(iso).toLocaleString(); }
}
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined,
      { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

/** Workspace fetch — clinic dashboard aggregate + the students list
 *  (used by the LogVisit modal's picker AND for resolving student
 *  names on visit cards). Both endpoints already exist; the clinic
 *  one is role-gated server-side. */
function useNurseWorkspace() {
  const [state, setState] = useState({
    loading: true,
    error: "",
    students: [],
    studentCount: 0,
    dashboard: {
      visitsToday: 0,
      visitsThisWeek: 0,
      currentlyInClinic: 0,
      studentsOnMedication: 0,
      studentsWithAllergies: 0,
      recentVisits: [],
    },
  });
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState(p => ({ ...p, loading: true, error: "" }));
    const safe = (promise, fallback) => promise.then(r => r.ok ? r.json() : fallback).catch(() => fallback);
    Promise.all([
      safe(fetch(`${API_BASE}/api/students?all=true`), []),
      safe(fetch(`${API_BASE}/api/clinic/dashboard`),
            { visitsToday: 0, visitsThisWeek: 0, currentlyInClinic: 0,
              studentsOnMedication: 0, studentsWithAllergies: 0, recentVisits: [] }),
    ]).then(([students, dash]) => {
      if (cancelled) return;
      const studentArr = Array.isArray(students) ? students : [];
      const active = studentArr.filter(s =>
        String(s.status || "ENROLLED").toUpperCase() === "ENROLLED"
      ).length;
      setState({
        loading: false,
        error: "",
        students: studentArr,
        studentCount: active,
        dashboard: dash || { visitsToday: 0, visitsThisWeek: 0, currentlyInClinic: 0,
                             studentsOnMedication: 0, studentsWithAllergies: 0, recentVisits: [] },
      });
    }).catch(e => {
      if (!cancelled) setState(p => ({ ...p, loading: false, error: e.message || "Failed to load" }));
    });
    return () => { cancelled = true; };
  }, [tick]);

  return { ...state, refresh };
}

export default function NurseDashboard({ user }) {
  const { loading, students, studentCount, dashboard, refresh } = useNurseWorkspace();
  const [logOpen, setLogOpen] = useState(false);
  const [actingVisitId, setActingVisitId] = useState(null);
  const navigate = useNavigate();

  const displayName = user?.linkedEntity?.displayName || user?.username || "Nurse";

  const studentsById = useMemo(
    () => Object.fromEntries(students.map(s => [s.id, s])),
    [students]
  );

  /** Mark a visit as "student returned to class" — server stamps the
   *  current time. Updates the dashboard count immediately on success. */
  async function markReturned(visitId) {
    if (actingVisitId) return;
    setActingVisitId(visitId);
    try {
      const res = await fetch(`${API_BASE}/api/clinic/visits/${visitId}/return`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (res.ok) refresh();
    } catch { /* non-fatal — the next refresh tick will catch up */ }
    finally { setActingVisitId(null); }
  }

  return (
    <RoleDashboard
      eyebrow="Nurse · Clinic"
      title={`Welcome, ${displayName}.`}
      sub="Live clinic snapshot — students under your care, today's visit count, and who's currently in. Log a new visit any time from the button below; tile counts update the moment you save."
      user={user}
    >
      {/* ── Real stat tiles + still-honest "coming soon" tiles ── */}
      <div className={m.statRow}>
        <Stat
          label="Students enrolled"
          value={loading ? "…" : studentCount}
          hint={loading ? "—" : "Active in this school year"}
        />
        <Stat
          label="Visits · today"
          value={loading ? "…" : dashboard.visitsToday}
          hint={loading ? "—"
            : dashboard.visitsToday === 0
              ? "No clinic visits logged yet today"
              : `${dashboard.visitsThisWeek} in the last 7 days`}
          tone={dashboard.visitsToday > 0 ? "info" : "none"}
        />
        <Stat
          label="Currently in clinic"
          value={loading ? "…" : dashboard.currentlyInClinic}
          hint={loading ? "—"
            : dashboard.currentlyInClinic === 0
              ? "Everyone back to class"
              : `${dashboard.currentlyInClinic} not yet released`}
          tone={dashboard.currentlyInClinic > 0 ? "warn" : "good"}
        />
        <Stat
          label="On medication"
          value={loading ? "…" : dashboard.studentsOnMedication}
          hint={loading ? "—"
            : dashboard.studentsOnMedication === 0
              ? "No active prescriptions on file"
              : "Students with an active medication"}
          tone={dashboard.studentsOnMedication > 0 ? "info" : "none"}
        />
        <Stat
          label="Allergies on file"
          value={loading ? "…" : dashboard.studentsWithAllergies}
          hint={loading ? "—"
            : dashboard.studentsWithAllergies === 0
              ? "No allergies recorded yet"
              : "Students with at least one allergy"}
          tone={dashboard.studentsWithAllergies > 0 ? "warn" : "none"}
        />
      </div>

      {/* ── Action bar — primary CTA + announcements link ──── */}
      <section className={m.actionCard}>
        <div className={m.actionLeft}>
          <div className={m.eyebrow}>Right now</div>
          <h3 className={m.actionTitle}>Log a clinic visit</h3>
          <p className={m.actionBody}>
            Capture every walk-in — complaint, treatment, and a short
            note. Mark the student as <em>returned to class</em> when
            they leave; the count above updates in real time.
          </p>
        </div>
        <div className={m.actionRight}>
          <button type="button" className={m.actionBtn}
                  onClick={() => setLogOpen(true)}
                  disabled={loading || students.length === 0}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.4"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5"  x2="12" y2="19"/>
              <line x1="5"  y1="12" x2="19" y2="12"/>
            </svg>
            New visit
          </button>
          <button type="button" className={m.actionBtnGhost}
                  onClick={() => navigate("/clinic/students")}>
            Health profiles →
          </button>
        </div>
      </section>

      {/* ── Recent visits — REAL data ──────────────────────── */}
      <section className={m.recentCard}>
        <header className={m.recentHead}>
          <div>
            <h3 className={m.recentTitle}>Recent visits</h3>
            <p className={m.recentSub}>
              Newest 8 clinic walk-ins. Click <em>Mark returned</em> when
              a student is sent back to class — the "currently in clinic"
              tile decrements live.
            </p>
          </div>
        </header>

        {loading ? (
          <div className={m.skel}>
            <div className={m.skelRow} /><div className={m.skelRow} /><div className={m.skelRow} />
          </div>
        ) : dashboard.recentVisits.length === 0 ? (
          <div className={m.empty}>
            <div className={m.emptyTitle}>No visits logged yet</div>
            <div className={m.emptySub}>
              When you record a clinic visit, it'll appear here with a quick
              "mark returned" button.
            </div>
          </div>
        ) : (
          <ul className={m.visitList}>
            {dashboard.recentVisits.map((v, idx) => {
              const st = studentsById[v.studentId];
              const fullName = st
                ? `${st.lastName || ""}, ${st.firstName || ""}`.replace(/^, |, $/g, "").trim()
                : `Student #${v.studentId}`;
              const inClinic = !v.returnedAt;
              return (
                <li
                  key={v.id}
                  className={`${m.visitRow} ${inClinic ? m.visitRowActive : ""}`}
                  style={{ animationDelay: `${Math.min(idx, 8) * 35}ms` }}>
                  <div className={m.visitAvatar}
                       style={{ background: avatarColor(fullName) }}>
                    {initials(st?.firstName, st?.lastName)}
                  </div>
                  <div className={m.visitBody}>
                    <div className={m.visitTopRow}>
                      <div className={m.visitName} title={fullName}>{fullName}</div>
                      {st?.studentNumber && (
                        <span className={m.visitNumber}>#{st.studentNumber}</span>
                      )}
                      {inClinic ? (
                        <span className={`${m.visitStatus} ${m.visitStatusIn}`}>In clinic</span>
                      ) : (
                        <span className={`${m.visitStatus} ${m.visitStatusOut}`}>Returned</span>
                      )}
                    </div>
                    <div className={m.visitMeta}>
                      <span>{fmtRelative(v.visitedAt)}</span>
                      <span className={m.visitSep}>·</span>
                      <span title={v.visitedAt
                        ? new Date(v.visitedAt).toLocaleString() : ""}>
                        in at {fmtTime(v.visitedAt)}
                      </span>
                      {v.returnedAt && (
                        <>
                          <span className={m.visitSep}>·</span>
                          <span>out at {fmtTime(v.returnedAt)}</span>
                        </>
                      )}
                    </div>
                    {(v.complaint || v.treatment) && (
                      <div className={m.visitDetail}>
                        {v.complaint && (
                          <>
                            <strong>Complaint:</strong> {v.complaint}
                          </>
                        )}
                        {v.complaint && v.treatment && <span className={m.visitSep}>·</span>}
                        {v.treatment && (
                          <>
                            <strong>Treatment:</strong> {v.treatment}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={m.visitActions}>
                    {inClinic ? (
                      <button type="button"
                              className={m.markReturnedBtn}
                              onClick={() => markReturned(v.id)}
                              disabled={actingVisitId === v.id}>
                        {actingVisitId === v.id ? "Saving…" : "Mark returned"}
                      </button>
                    ) : (
                      <span className={m.releasedHint}>
                        {fmtRelative(v.returnedAt)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── Log Visit modal — opens from the New visit button ── */}
      <LogVisitModal
        open={logOpen}
        students={students}
        onClose={() => setLogOpen(false)}
        onSaved={() => { setLogOpen(false); refresh(); }}
      />
    </RoleDashboard>
  );
}

function Stat({ label, value, hint, tone, comingSoon }) {
  return (
    <div className={`${m.stat} ${comingSoon ? m.statComingSoon : ""} ${tone ? m["stat_" + tone] : ""}`}>
      <div className={m.statLabel}>{label}</div>
      <div className={m.statValue}>
        {value == null ? <span className={m.statDash}>—</span> : value}
      </div>
      <div className={m.statHint}>
        {comingSoon && <span className={m.comingPill}>Coming soon</span>}
        {hint}
      </div>
    </div>
  );
}
